import { useLayoutEffect, useMemo, useState } from "react";

export const documentRowKey = (row: any) => `${row.rowType || "item"}-${row.id}`;

export function paginateDocumentRows(rows: any[], heights: Record<string, number>, baseHeight: number) {
  const pages: any[][] = [];
  let page: any[] = [];
  let used = 0;
  rows.forEach((row, index) => {
    // Conservative first render; replace estimates with actual wrapped DOM heights.
    const note = row.production_notes || row.supplierNote || row.supplier_note || "";
    const estimate = (row.rowType === "header" ? 60 : baseHeight) +
      (note ? (Math.ceil(note.length / 70) + note.split("\n").length) * 30 : 0);
    const height = Math.ceil(heights[documentRowKey(row)] || estimate);
    const budget = pages.length === 0 ? 450 : 700;
    const following = rows[index + 1];
    const followingHeight = row.rowType === "header" && following
      ? (heights[documentRowKey(following)] || baseHeight) : 0;
    if (page.length && used + height + followingHeight > budget) {
      pages.push(page);
      page = [];
      used = 0;
    }
    page.push(row);
    used += height;
  });
  if (page.length) pages.push(page);
  return pages;
}

export function documentNoteOverflows(note: HTMLElement, text: HTMLElement) {
  return text.getBoundingClientRect().bottom > note.getBoundingClientRect().bottom + 1 ||
    text.scrollWidth > note.clientWidth + 1 || text.scrollHeight > note.clientHeight + 1;
}

export function selectDocumentPages(rows: any[], heights: Record<string, number>, baseHeight: number,
  finalSection: boolean, legacyPages: any[], overflow: boolean) {
  if (!overflow) return legacyPages;
  const result: any[] = paginateDocumentRows(rows, heights, baseHeight);
  const last = result[result.length - 1];
  const used = last?.reduce((total: number, row: any) => total + (heights[documentRowKey(row)] || baseHeight), 0) || 0;
  if (finalSection && last && used > (result.length === 1 ? 450 : 700) - 230) result.push(-1);
  return result;
}

export default function useDocumentPages(rows: any[], pageRef: any, baseHeight: number, finalSection: boolean, legacyPages: any[] = []) {
  const [overflow, setOverflow] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const pages = useMemo(() => selectDocumentPages(rows, heights, baseHeight, finalSection, legacyPages, overflow),
    [rows, heights, baseHeight, finalSection, legacyPages, overflow]);
  useLayoutEffect(() => {
    pageRef.current.length = pages.length;
    const measured: Record<string, number> = {};
    let hasOverflow = false;
    pageRef.current.forEach((page: HTMLElement | null) => {
      page?.querySelectorAll<HTMLElement>("[data-pdf-row-key]").forEach(row => {
        row.classList.remove("pdf-overflow-row", "pdf-overflow-header");
        const note = row.querySelector<HTMLElement>(".pdf-item-note");
        const text = note?.querySelector<HTMLElement>("p");
        if (note && text && documentNoteOverflows(note, text)) {
          row.classList.add("pdf-overflow-row");
          hasOverflow = true;
        }
        if (row.classList.contains("pdf-section-header") && Array.from(row.querySelectorAll<HTMLElement>("strong, span")).some(element => element.scrollWidth > element.clientWidth + 1)) {
          row.classList.add("pdf-overflow-header");
          hasOverflow = true;
        }
        measured[row.dataset.pdfRowKey!] = Math.ceil(row.getBoundingClientRect().height);
      });
    });
    if (hasOverflow !== overflow) setOverflow(hasOverflow);
    if (Object.keys(measured).some(key => measured[key] > (heights[key] || 0))) {
      setHeights(previous => Object.fromEntries(Object.entries({ ...previous, ...measured })
        .map(([key, height]) => [key, Math.max(height, previous[key] || 0)])));
    }
  });
  return pages;
}
